import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Table from "../../../components/table";
import Badge from "../../../components/badge";
import Button from "../../../components/button";
import Modal from "../../../components/modal";
import Tabs, { TabItem } from "../../../components/tabs";
import { Select } from "../../../components/input-functions";
import SearchableMultiSelect, { MultiSelectOption } from "../../../components/searchable-multi-select";
import IdLookup from "../../../components/id-lookup";
import useScreenName from "../../../hooks/useScreenName";
import { dateToDDMMYYYY, dateToDDMMYYYYHHMMAP, splitKeyWithSpace } from "../../../utils";
import dispatch from "../../../middleware";
import { getPolicyAttachmentCreationOptions } from "../../../actions/policy-attachment.action";
import { PolicyAttachmentCreationOptions } from "../../../models/underwritingPolicyAttachment";
import {
  createPreApproval,
  expirePreApproval,
  listPreApprovals,
} from "../../../actions/credit-decision.action";
import {
  CreatePreApprovalRequest,
  InterestRateTypeEnum,
  PreApproval,
  ProcessingFeeTypeEnum,
} from "../../../models/creditDecision";

type Tab = "list" | "create";
type UpdateStage = "form" | "confirm" | "submitting" | "done";

const tabItems: TabItem<Tab>[] = [
  { label: "List", value: "list" },
  { label: "Single Create", value: "create" },
];

const listColumns = [
  { field: "id", label: "ID" },
  { field: "workerId", label: "Worker" },
  { field: "employerId", label: "Employer" },
  { field: "amountRange", label: "Amount Range" },
  { field: "tenureRange", label: "Tenure (Months)" },
  { field: "pricing", label: "Pricing" },
  { field: "validity", label: "Valid Until" },
  { field: "status", label: "Status" },
  { field: "actions", label: "Actions" },
];

function formatDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultCreateForm() {
  const today = new Date();
  const validUntilDate = new Date(today);
  validUntilDate.setDate(validUntilDate.getDate() + 6);
  return {
    workerId: "",
    employerId: "",
    amountMin: "1000",
    amountMax: "",
    tenureMin: "1",
    tenureMax: "",
    irpa: "19.99",
    processingFee: "0",
    processingFeeType: ProcessingFeeTypeEnum.FIXED as ProcessingFeeTypeEnum,
    interestRateType: InterestRateTypeEnum.REDUCING_BALANCE_RATE as InterestRateTypeEnum,
    policies: [] as string[],
    validFrom: formatDateInput(today),
    validUntil: formatDateInput(validUntilDate),
    remarks: "Manual Exception",
  };
}

function getTime(date?: string) {
  if (!date) return NaN;
  return new Date(date).getTime();
}

function getPreApprovalState(pa: PreApproval) {
  if (pa.expiredAt) return "expired";
  const now = Date.now();
  const validFrom = getTime(pa.validFrom);
  const validUntil = getTime(pa.validUntil);
  if (Number.isFinite(validFrom) && validFrom > now) return "scheduled";
  if (Number.isFinite(validUntil) && validUntil < now) return "expired";
  return "active";
}

function parseRequiredNumber(value: string, label: string) {
  if (!value.trim()) throw new Error(`${label} is required`);
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) throw new Error(`${label} must be a valid number`);
  return parsed;
}

interface PreApprovalForm {
  workerId: string;
  employerId: string;
  amountMin: string;
  amountMax: string;
  tenureMin: string;
  tenureMax: string;
  irpa: string;
  processingFee: string;
  processingFeeType: ProcessingFeeTypeEnum;
  interestRateType: InterestRateTypeEnum;
  policies: string[];
  validFrom: string;
  validUntil: string;
  remarks: string;
}

function preApprovalToForm(pa: PreApproval): PreApprovalForm {
  return {
    workerId: pa.workerId,
    employerId: pa.employerId,
    amountMin: String(pa.amount.min),
    amountMax: String(pa.amount.max),
    tenureMin: String(pa.tenureInMonths.min),
    tenureMax: String(pa.tenureInMonths.max),
    irpa: String(pa.pricing.irpa),
    processingFee: String(pa.pricing.processingFee),
    processingFeeType: pa.pricing.processingFeeType,
    interestRateType: pa.pricing.interestRateType,
    policies: pa.policies ?? [],
    validFrom: formatDateInput(new Date(pa.validFrom)),
    validUntil: formatDateInput(new Date(pa.validUntil)),
    remarks: pa.remarks ?? "",
  };
}

function diffForm(original: PreApprovalForm, current: PreApprovalForm) {
  const changes: Array<{ field: string; old: string; new: string }> = [];
  const scalarFields: (keyof PreApprovalForm)[] = [
    "workerId",
    "employerId",
    "amountMin",
    "amountMax",
    "tenureMin",
    "tenureMax",
    "irpa",
    "processingFee",
    "processingFeeType",
    "interestRateType",
    "validFrom",
    "validUntil",
    "remarks",
  ];
  for (const field of scalarFields) {
    if (String(original[field]).trim() !== String(current[field]).trim()) {
      changes.push({ field, old: String(original[field]), new: String(current[field]) });
    }
  }
  const originalPolicies = original.policies.slice().sort().join(",");
  const currentPolicies = current.policies.slice().sort().join(",");
  if (originalPolicies !== currentPolicies) {
    changes.push({ field: "policies", old: originalPolicies || "-", new: currentPolicies || "-" });
  }
  return changes;
}

function FormInput({
  label,
  value,
  onChange,
  type = "text",
  readOnly = false,
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: string;
  readOnly?: boolean;
}) {
  return (
    <div>
      <label className="form-label">{label}</label>
      <input
        type={type}
        className="form-control"
        value={value}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Field({
  form,
  setForm,
  label,
  field,
  type = "text",
}: {
  form: PreApprovalForm;
  setForm: React.Dispatch<React.SetStateAction<PreApprovalForm>>;
  label: string;
  field: Extract<keyof PreApprovalForm, "amountMin" | "amountMax" | "tenureMin" | "tenureMax" | "irpa" | "processingFee" | "validFrom" | "validUntil" | "remarks">;
  type?: string;
}) {
  return (
    <FormInput
      label={label}
      type={type}
      value={form[field]}
      onChange={(value) => setForm((p) => ({ ...p, [field]: value }))}
    />
  );
}

function SelectField({
  form,
  setForm,
  label,
  field,
  options,
}: {
  form: PreApprovalForm;
  setForm: React.Dispatch<React.SetStateAction<PreApprovalForm>>;
  label: string;
  field: "processingFeeType" | "interestRateType";
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="form-label">{label}</label>
      <Select
        value={form[field]}
        onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value as PreApprovalForm[typeof field] }))}
        options={options}
      />
    </div>
  );
}

function PreApprovalFormFields({
  form,
  setForm,
  readOnlyWorkerEmployer = false,
  policyOptions,
}: {
  form: PreApprovalForm;
  setForm: React.Dispatch<React.SetStateAction<PreApprovalForm>>;
  readOnlyWorkerEmployer?: boolean;
  policyOptions: MultiSelectOption[];
}) {
  return (
    <div className="row g-3">
      <div className="col-12 col-md-6">
        <FormInput
          label="Worker ID *"
          value={form.workerId}
          readOnly={readOnlyWorkerEmployer}
          onChange={(value) => setForm((p) => ({ ...p, workerId: value }))}
        />
      </div>
      {!readOnlyWorkerEmployer && (
        <div className="col-12 col-md-6 d-flex align-items-end">
          <IdLookup onSelect={({ workerId, employerId }) => setForm((p) => ({ ...p, workerId, employerId }))} />
        </div>
      )}
      <div className="col-12 col-md-6">
        <FormInput
          label="Employer ID *"
          value={form.employerId}
          readOnly={readOnlyWorkerEmployer}
          onChange={(value) => setForm((p) => ({ ...p, employerId: value }))}
        />
      </div>
      <div className="col-12 col-md-6 col-lg-3"><Field form={form} setForm={setForm} label="Amount Min *" field="amountMin" type="number" /></div>
      <div className="col-12 col-md-6 col-lg-3"><Field form={form} setForm={setForm} label="Amount Max *" field="amountMax" type="number" /></div>
      <div className="col-12 col-md-6 col-lg-3"><Field form={form} setForm={setForm} label="Tenure Min (Months) *" field="tenureMin" type="number" /></div>
      <div className="col-12 col-md-6 col-lg-3"><Field form={form} setForm={setForm} label="Tenure Max (Months) *" field="tenureMax" type="number" /></div>
      <div className="col-12 col-md-6 col-lg-3"><Field form={form} setForm={setForm} label="IRPA *" field="irpa" type="number" /></div>
      <div className="col-12 col-md-6 col-lg-3"><Field form={form} setForm={setForm} label="Processing Fee *" field="processingFee" type="number" /></div>
      <div className="col-12 col-md-6 col-lg-3">
        <SelectField
          form={form}
          setForm={setForm}
          label="Processing Fee Type *"
          field="processingFeeType"
          options={Object.values(ProcessingFeeTypeEnum).map((v) => ({ value: v, label: splitKeyWithSpace(v) }))}
        />
      </div>
      <div className="col-12 col-md-6 col-lg-3">
        <SelectField
          form={form}
          setForm={setForm}
          label="Interest Rate Type *"
          field="interestRateType"
          options={Object.values(InterestRateTypeEnum).map((v) => ({ value: v, label: splitKeyWithSpace(v) }))}
        />
      </div>
      <div className="col-12">
        <label className="form-label">Policies</label>
        <SearchableMultiSelect
          options={policyOptions}
          placeholder="Select policies"
          value={form.policies}
          onChange={(values) => setForm((p) => ({ ...p, policies: values.map(String) }))}
          search
          selectAll={false}
          showBadges
        />
      </div>
      <div className="col-12 col-md-6"><Field form={form} setForm={setForm} label="Valid From" field="validFrom" type="date" /></div>
      <div className="col-12 col-md-6"><Field form={form} setForm={setForm} label="Valid Until *" field="validUntil" type="date" /></div>
      <div className="col-12"><Field form={form} setForm={setForm} label="Remarks" field="remarks" /></div>
    </div>
  );
}

export default function PreApprovalsPage() {
  useScreenName("Pre-Approvals");
  const storeDispatch = useDispatch();

  const [tab, setTab] = useState<Tab>("list");
  const [preApprovals, setPreApprovals] = useState<PreApproval[]>([]);
  const [listCursor, setListCursor] = useState<string | undefined>();
  const [listHasMore, setListHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [workerIdFilter, setWorkerIdFilter] = useState<string>("");
  const [employerIdFilter, setEmployerIdFilter] = useState<string>("");
  const [statusInput, setStatusInput] = useState<string>("active");
  const [workerIdInput, setWorkerIdInput] = useState<string>("");
  const [employerIdInput, setEmployerIdInput] = useState<string>("");
  const [createForm, setCreateForm] = useState(getDefaultCreateForm);
  const [createLoading, setCreateLoading] = useState(false);
  const [createResult, setCreateResult] = useState<string>("");
  const [expireLoadingId, setExpireLoadingId] = useState<string | null>(null);
  const [selectedPreApproval, setSelectedPreApproval] = useState<PreApproval | null>(null);
  const [editingPreApproval, setEditingPreApproval] = useState<PreApproval | null>(null);
  const [editForm, setEditForm] = useState<PreApprovalForm | null>(null);
  const [editOriginalForm, setEditOriginalForm] = useState<PreApprovalForm | null>(null);
  const [editStage, setEditStage] = useState<UpdateStage>("form");
  const [editError, setEditError] = useState<string>("");
  const [editNewPreApprovalId, setEditNewPreApprovalId] = useState<string>("");
  const [creationOptions, setCreationOptions] = useState<PolicyAttachmentCreationOptions>({
    employers: [],
    availablePolicies: [],
  });
  const initFetchedRef = useRef(false);

  const policyOptions: MultiSelectOption[] = useMemo(
    () =>
      creationOptions.availablePolicies
        .filter((policy) => policy.name.includes("PRE_APPROVAL"))
        .map((policy) => ({
          value: policy.name,
          text: policy.name,
          html: `<strong>${policy.name}</strong><br><small style="color: #666;">${policy.description}</small>`,
        })),
    [creationOptions.availablePolicies]
  );

  useEffect(() => {
    dispatch(storeDispatch, getPolicyAttachmentCreationOptions())
      .then((options: PolicyAttachmentCreationOptions) => setCreationOptions(options))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openEditModal(pa: PreApproval) {
    const initial = preApprovalToForm(pa);
    setEditingPreApproval(pa);
    setEditForm(initial);
    setEditOriginalForm(initial);
    setEditStage("form");
    setEditError("");
    setEditNewPreApprovalId("");
  }

  function closeEditModal() {
    setEditingPreApproval(null);
    setEditForm(null);
    setEditOriginalForm(null);
    setEditStage("form");
    setEditError("");
    setEditNewPreApprovalId("");
  }

  function applyFilters() {
    setStatusFilter(statusInput);
    setWorkerIdFilter(workerIdInput);
    setEmployerIdFilter(employerIdInput);
  }

  function clearFilters() {
    setStatusInput("");
    setWorkerIdInput("");
    setEmployerIdInput("");
    setStatusFilter("");
    setWorkerIdFilter("");
    setEmployerIdFilter("");
  }

  async function fetchList(reset?: boolean) {
    try {
      setListLoading(true);
      const query: Record<string, any> = { limit: 20 };
      if (statusFilter === "active") query.isActive = true;
      if (statusFilter === "inactive") query.isActive = false;
      if (workerIdFilter.trim()) query.workerId = workerIdFilter.trim();
      if (employerIdFilter.trim()) query.employerId = employerIdFilter.trim();
      if (!reset && listCursor) query.cursor = listCursor;
      const response = await dispatch(storeDispatch, listPreApprovals(query));
      if (!response || !Array.isArray(response.data)) {
        throw new Error("Invalid response from server");
      }
      setPreApprovals((prev) => (reset ? response.data : [...prev, ...response.data]));
      setListCursor(response.nextCursor);
      setListHasMore(response.hasMore ?? false);
    } catch (error) {
      console.error("Error fetching pre-approvals:", error);
      alert(`Failed to fetch pre-approvals: ${extractErrorMessage(error)}`);
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    if (!initFetchedRef.current) {
      initFetchedRef.current = true;
      fetchList(true);
      return;
    }
    fetchList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, workerIdFilter, employerIdFilter]);

  function validateForm(form: PreApprovalForm): string | null {
    try {
      if (!form.workerId.trim()) return "Worker ID is required";
      if (!form.employerId.trim()) return "Employer ID is required";
      if (!form.validUntil) return "Valid Until is required";
      buildCreatePayload(form);
      return null;
    } catch (error) {
      return extractErrorMessage(error);
    }
  }

  async function handleCreate() {
    setCreateResult("");
    const validationError = validateForm(createForm);
    if (validationError) {
      setCreateResult(`Error: ${validationError}`);
      return;
    }

    setCreateLoading(true);
    try {
      const payload = buildCreatePayload(createForm);
      const result = await dispatch(storeDispatch, createPreApproval(payload));
      setCreateResult(`Pre-approval created: ${result?._id ?? "(unknown id)"}`);
      setCreateForm(getDefaultCreateForm());
      await fetchList(true);
    } catch (error) {
      setCreateResult(`Error: ${extractErrorMessage(error)}`);
    } finally {
      setCreateLoading(false);
    }
  }

  function buildCreatePayload(form: PreApprovalForm): CreatePreApprovalRequest {
    const amountMin = parseRequiredNumber(form.amountMin, "Amount Min");
    const amountMax = parseRequiredNumber(form.amountMax, "Amount Max");
    const tenureMin = parseRequiredNumber(form.tenureMin, "Tenure Min");
    const tenureMax = parseRequiredNumber(form.tenureMax, "Tenure Max");
    const irpa = parseRequiredNumber(form.irpa, "IRPA");
    const processingFee = parseRequiredNumber(form.processingFee, "Processing Fee");
    if (amountMin > amountMax) throw new Error("Amount Min cannot be greater than Amount Max");
    if (tenureMin > tenureMax) throw new Error("Tenure Min cannot be greater than Tenure Max");

    const payload: CreatePreApprovalRequest = {
      workerId: form.workerId.trim(),
      employerId: form.employerId.trim(),
      amount: { min: amountMin, max: amountMax },
      tenureInMonths: { min: tenureMin, max: tenureMax },
      pricing: {
        irpa,
        processingFee,
        processingFeeType: form.processingFeeType,
        interestRateType: form.interestRateType,
      },
      validFrom: new Date().toISOString(),
      validUntil: form.validUntil,
    };
    if (form.policies.length > 0) payload.policies = form.policies;
    if (form.remarks.trim()) payload.remarks = form.remarks.trim();
    return payload;
  }

  function validateEditForm(): string | null {
    if (!editForm) return "Form is not loaded";
    if (editForm.workerId !== editingPreApproval?.workerId || editForm.employerId !== editingPreApproval?.employerId) {
      return "Worker ID and Employer ID cannot be changed";
    }
    return validateForm(editForm);
  }

  function hasEditChanges() {
    if (!editOriginalForm || !editForm) return false;
    return diffForm(editOriginalForm, editForm).length > 0;
  }

  function handleEditConfirm() {
    const validationError = validateEditForm();
    if (validationError) {
      setEditError(validationError);
      return;
    }
    setEditError("");
    setEditStage("confirm");
  }

  async function handleEditSubmit() {
    if (!editingPreApproval || !editForm) return;
    const validationError = validateEditForm();
    if (validationError) {
      setEditError(validationError);
      setEditStage("form");
      return;
    }
    if (!hasEditChanges()) {
      setEditError("No changes to update");
      return;
    }

    setEditStage("submitting");
    setEditError("");
    try {
      const expireReason = `Updating pre-approval via edit. New pre-approval will be created with updated terms.`;
      const expireResponse = await dispatch(
        storeDispatch,
        expirePreApproval(editingPreApproval._id, { reason: expireReason })
      );
      if (!expireResponse?.expired) {
        throw new Error("Failed to expire the existing pre-approval");
      }

      const payload = buildCreatePayload(editForm);
      const createResponse = await dispatch(storeDispatch, createPreApproval(payload));
      const newPreApprovalId = createResponse?._id;
      if (!newPreApprovalId) {
        throw new Error("Old pre-approval was expired, but failed to create the new one. Please create a new pre-approval manually.");
      }

      setEditNewPreApprovalId(newPreApprovalId);
      setEditStage("done");
      await fetchList(true);
      setCreateResult(`Pre-approval updated: expired ${editingPreApproval._id}, created ${newPreApprovalId}`);
    } catch (error: any) {
      setEditStage("form");
      setEditError(`Error: ${extractErrorMessage(error)}`);
    }
  }

  function extractErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof (error as Record<string, unknown>).message === "string"
    ) {
      return (error as Record<string, unknown>).message as string;
    }
    return "Something went wrong";
  }

  async function handleExpire(pa: PreApproval) {
    const reason = prompt("Enter reason for expiring this pre-approval:");
    if (reason === null) return;
    if (!reason.trim()) {
      alert("Reason is required to expire a pre-approval");
      return;
    }
    try {
      setExpireLoadingId(pa._id);
      const response = await dispatch(storeDispatch, expirePreApproval(pa._id, { reason: reason.trim() }));
      if (response.expired) await fetchList(true);
      else alert("Pre-approval not found or already expired");
    } catch (error: any) {
      alert(`Error: ${extractErrorMessage(error)}`);
    } finally {
      setExpireLoadingId(null);
    }
  }

  function getStatusBadge(pa: PreApproval) {
    const state = getPreApprovalState(pa);
    if (state === "scheduled") return <Badge type="pending" capitalize>Scheduled</Badge>;
    if (state === "expired") return <Badge type="reject" capitalize>Expired</Badge>;
    return <Badge type="success" capitalize>Active</Badge>;
  }

  function renderDetailModal() {
    const pa = selectedPreApproval;
    if (!pa) return null;
    const rows: Array<[string, any]> = [
      ["ID", pa._id],
      ["Worker ID", pa.workerId],
      ["Employer ID", pa.employerId],
      ["Amount", `${pa.amount.min} - ${pa.amount.max}`],
      ["Tenure (Months)", `${pa.tenureInMonths.min} - ${pa.tenureInMonths.max}`],
      ["IRPA", pa.pricing.irpa],
      ["Processing Fee", `${pa.pricing.processingFee} (${splitKeyWithSpace(pa.pricing.processingFeeType)})`],
      ["Interest Rate Type", splitKeyWithSpace(pa.pricing.interestRateType)],
      ["Policies", pa.policies?.join(", ") || "-"],
      ["Valid From", pa.validFrom ? dateToDDMMYYYYHHMMAP(pa.validFrom) : "-"],
      ["Valid Until", pa.validUntil ? dateToDDMMYYYYHHMMAP(pa.validUntil) : "-"],
      ["Created By", pa.createdBy || "-"],
      ["Remarks", pa.remarks || "-"],
      ["Expired At", pa.expiredAt ? dateToDDMMYYYYHHMMAP(pa.expiredAt) : "-"],
      ["Expired By", pa.expiredBy || "-"],
      ["Expiry Reason", pa.expiryReason || "-"],
      ["Expiry Actor", pa.expiryActor || "-"],
      ["Expiry Source Ref ID", pa.expirySourceRefId || "-"],
    ];

    return (
      <Modal open={!!pa} onClose={() => setSelectedPreApproval(null)} title="Pre-Approval Detail" hasSubmitButton={false} hasCancelButton size="large">
        <table className="table table-sm table-bordered">
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td className="fw-bold" style={{ width: "30%" }}>{label}</td>
                <td style={{ wordBreak: "break-word" }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    );
  }

  function renderEditModal() {
    if (!editingPreApproval || !editForm || !editOriginalForm) return null;

    const changes = diffForm(editOriginalForm, editForm);
    const isFormStage = editStage === "form";
    const isConfirmStage = editStage === "confirm";
    const isSubmitting = editStage === "submitting";
    const isDone = editStage === "done";

    const modalTitle = isDone
      ? "Update Complete"
      : isConfirmStage
      ? "Confirm Update"
      : "Update Pre-Approval";

    function renderFormBody() {
      if (!editForm) return null;
      return (
        <PreApprovalFormFields
          form={editForm}
          setForm={setEditForm as React.Dispatch<React.SetStateAction<PreApprovalForm>>}
          readOnlyWorkerEmployer
          policyOptions={policyOptions}
        />
      );
    }

    function renderConfirmBody() {
      if (!editingPreApproval) return null;
      return (
        <div>
          <p>
            This will <strong>expire</strong> old pre-approval{" "}
            <code>{editingPreApproval._id}</code> and create a new one with the changes below.
          </p>
          {changes.length === 0 ? (
            <p className="text-muted">No changes detected.</p>
          ) : (
            <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Old</th>
                  <th>New</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((change) => (
                  <tr key={change.field}>
                    <td className="fw-bold">{splitKeyWithSpace(change.field)}</td>
                    <td style={{ wordBreak: "break-word" }}>{change.old}</td>
                    <td style={{ wordBreak: "break-word" }}>{change.new}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    return (
      <Modal
        open={!!editingPreApproval}
        onClose={closeEditModal}
        title={modalTitle}
        size="large"
        hasSubmitButton={isFormStage || isConfirmStage}
        submitButtonText={isConfirmStage ? "Confirm Update" : "Review Changes"}
        submitButtonDisable={isFormStage ? !hasEditChanges() : false}
        hasSubmitButtonLoading={isSubmitting}
        onSubmit={isConfirmStage ? handleEditSubmit : handleEditConfirm}
        hasCancelButton
        cancelButtonText={isDone ? "Close" : isConfirmStage ? "Back" : "Cancel"}
        isCancelButtonLoading={isSubmitting}
        onCancel={isConfirmStage ? () => setEditStage("form") : closeEditModal}
      >
        {isSubmitting && <p>Expiring old pre-approval and creating a new one...</p>}
        {isDone && (
          <div className="alert alert-success">
            <p className="mb-1">Pre-approval updated. The old pre-approval has been expired and a new one created.</p>
            {editNewPreApprovalId && (
              <p className="mb-0">
                New Pre-Approval ID: <code>{editNewPreApprovalId}</code>
              </p>
            )}
          </div>
        )}
        {!isSubmitting && !isDone && (isFormStage ? renderFormBody() : renderConfirmBody())}
        {editError && <div className="alert alert-danger mt-3">{editError}</div>}
      </Modal>
    );
  }

  return (
    <div>
      <h2 className="mb-4">Pre-Approvals</h2>
      <div className="dashboard-container">
        <div className="mb-4">
          <Tabs items={tabItems} value={tab} onChange={setTab} />
        </div>

        {tab === "list" && (
          <div>
            <div className="row g-3 mb-3 align-items-end">
              <div className="col-12 col-md-4 col-lg-3">
                <label className="form-label">Status Filter</label>
                <Select
                  value={statusInput}
                  onChange={(e) => setStatusInput(e.target.value)}
                  options={[{ value: "", label: "All" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive/Expired" }]}
                />
              </div>
              <div className="col-12 col-md-4 col-lg-3">
                <label className="form-label">Worker ID</label>
                <input type="text" className="form-control" placeholder="Filter by worker ID" value={workerIdInput} onChange={(e) => setWorkerIdInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }} />
              </div>
              <div className="col-12 col-md-4 col-lg-3">
                <label className="form-label">Employer ID</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter by employer ID"
                  value={employerIdInput}
                  onChange={(e) => setEmployerIdInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
                />
              </div>
              <div className="col-12 col-md-12 col-lg-3 d-flex gap-2">
                <Button variant="primary" onClick={applyFilters} disabled={listLoading}>Apply</Button>
                <Button variant="blue-outline" onClick={clearFilters} disabled={listLoading}>Clear</Button>
              </div>
            </div>
            <Table
              loading={listLoading}
              columns={listColumns}
              data={preApprovals.map((pa) => {
                const isActive = getPreApprovalState(pa) === "active";
                return {
                  id: <Button variant="blue-text" size="xs" onClick={() => setSelectedPreApproval(pa)}>{pa._id}</Button>,
                  workerId: pa.workerId,
                  employerId: pa.employerId,
                  amountRange: `${pa.amount.min} - ${pa.amount.max}`,
                  tenureRange: `${pa.tenureInMonths.min} - ${pa.tenureInMonths.max}`,
                  pricing: `IRPA: ${pa.pricing.irpa} | Fee: ${pa.pricing.processingFee} (${splitKeyWithSpace(pa.pricing.processingFeeType)})`,
                  validity: dateToDDMMYYYY(pa.validUntil),
                  status: getStatusBadge(pa),
                  actions: isActive ? (
                    <div className="d-flex" style={{ gap: "8px", minWidth: "max-content" }}>
                      <Button variant="primary" size="sm" onClick={() => openEditModal(pa)} style={{ padding: "4px 10px", fontSize: "13px" }}>
                        Edit
                      </Button>
                      <Button variant="danger-fill" size="sm" onClick={() => handleExpire(pa)} disabled={expireLoadingId === pa._id} style={{ padding: "4px 10px", fontSize: "13px" }}>
                        {expireLoadingId === pa._id ? "Expiring..." : "Expire"}
                      </Button>
                    </div>
                  ) : <span className="text-muted">—</span>,
                };
              })}
            />
            {listHasMore && (
              <div className="text-center mt-3">
                <Button variant="light-blue" onClick={() => fetchList(false)} disabled={listLoading}>{listLoading ? "Loading..." : "Load More"}</Button>
              </div>
            )}
          </div>
        )}

        {tab === "create" && (
          <div>
            <PreApprovalFormFields
              form={createForm}
              setForm={setCreateForm}
              policyOptions={policyOptions}
            />
            <div className="col-12 mt-3">
              <Button variant="primary" onClick={handleCreate} disabled={createLoading}>
                {createLoading ? "Creating..." : "Create Pre-Approval"}
              </Button>
            </div>
            {createResult && <div className="col-12"><div className="alert alert-info mt-3">{createResult}</div></div>}
          </div>
        )}
      </div>
      {renderDetailModal()}
      {renderEditModal()}
    </div>
  );
}
